import { authService } from './authService';

class PushService {
  // VAPID public key for push notifications
  private vapidPublicKey: string | null = null;

  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered successfully:', registration);
        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);

        // Check if it's an SSL certificate error
        if (error instanceof Error && error.message.includes('SSL') || error.message.includes('certificate')) {
          console.warn('SSL certificate error detected. For development, you may need to:');
          console.warn('1. Accept the self-signed certificate in your browser');
          console.warn('2. Or run the application on HTTP instead of HTTPS');
          console.warn('3. Or use a valid SSL certificate');
        }

        return null;
      }
    }
    return null;
  }

  private async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch('/api/push/vapid-key', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch VAPID public key');
    }

    const data = await response.json();
    this.vapidPublicKey = data.vapid_public_key;
    return this.vapidPublicKey!;
  }

  async subscribeToNotifications(): Promise<PushSubscription | null> {
    try {
      const registration = await this.registerServiceWorker();
      if (!registration) return null;

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }

      const vapidPublicKey = await this.getVapidPublicKey();

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('Push subscription created:', subscription);

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  async unsubscribeFromNotifications(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await this.removeSubscriptionFromBackend(subscription);
        console.log('Successfully unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
    }
  }

  private async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscription)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send subscription to backend');
    }

    const result = await response.json();
    console.log('Subscription sent to backend:', result);
  }

  private async removeSubscriptionFromBackend(subscription: PushSubscription): Promise<void> {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const response = await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });

    if (!response.ok) {
      console.error('Failed to remove subscription from backend');
    }
  }

  async getSubscriptionStatus(): Promise<{ isSubscribed: boolean; subscriptionCount: number }> {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return { isSubscribed: false, subscriptionCount: 0 };
      }

      const response = await fetch('/api/push/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        return { isSubscribed: false, subscriptionCount: 0 };
      }

      const result = await response.json();
      return {
        isSubscribed: result.is_subscribed || false,
        subscriptionCount: result.subscription_count || 0
      };
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return { isSubscribed: false, subscriptionCount: 0 };
    }
  }

  async unsubscribeAll(): Promise<void> {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch('/api/push/unsubscribe-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe from all notifications');
      }

      console.log('Successfully unsubscribed from all push notifications');
    } catch (error) {
      console.error('Failed to unsubscribe from all notifications:', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Check if push notifications are supported
  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  // Check if we're running on HTTPS (required for push notifications in production)
  isSecureContext(): boolean {
    return window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  }

  // Get development mode status
  isDevelopment(): boolean {
    return window.location.protocol === 'http:' && window.location.hostname === 'localhost';
  }
}

export const pushService = new PushService();